/**
 * Homepage capture band handler — Klaviyo
 *
 * Drives sections/cust-hp2-capture.liquid: a two-phase inline capture band.
 * Phase 1 takes the email and subscribes it to the Klaviyo email list; on a
 * confirmed 2xx the right-hand column swaps to Phase 2, which upsells SMS for
 * the bigger discount code and subscribes to the SMS list.
 *
 * Loaded as a static asset via {{ 'cust-hp2-capture.js' | asset_url |
 * script_tag }}, the same mechanism as assets/blog-subscribe-handler.js. It
 * used to be an inline <script> inside the section, which did not bind until
 * several seconds after load — every click in that window fell through to the
 * native Shopify customer form, created a Shopify contact, reached Klaviyo not
 * at all, and eventually tripped Shopify's bot challenge.
 *
 * Two consequences of that fix shape everything below:
 *
 *   1. Being a static asset, this file is not rendered by Liquid and cannot
 *      read {{ section.id }} or {{ template }}. Every piece of configuration —
 *      company id, list ids, discount codes, section id, template — is read off
 *      data-hp2c-* attributes on the section root, at event time.
 *
 *   2. Listeners are delegated from `document` in the CAPTURE phase rather than
 *      bound to the form or button nodes. There is no window in which the
 *      controls exist but are dead: the listener is attached before the band is
 *      even parsed, survives the theme editor re-rendering the section, and
 *      survives node swaps. Capture phase means nothing downstream can beat us
 *      to the event.
 *
 * Nothing is cached per-page. Every handler re-resolves its root from the event
 * target, so any number of bands can coexist on one page, each with its own
 * config and its own captured email.
 *
 * No API key: the Klaviyo client endpoint authenticates with the PUBLIC
 * company_id alone. Nothing secret is injected into the page.
 */
(function () {
  // The section renders this tag once per instance, so a page with two bands
  // loads the script twice. Executing twice would double-bind the delegated
  // listeners and fire two POSTs per click.
  if (window.__hp2CaptureBound) return;
  window.__hp2CaptureBound = true;

  var REVISION        = '2025-04-15';
  var CALL_TIMEOUT_MS = 2500;
  var RETRY_DELAY_MS  = 1000;

  // Per-band state, keyed by the root element rather than held in a single
  // global, so multiple instances never share a captured email. Not written to
  // the DOM: the address has no business sitting in an attribute.
  var states = new WeakMap();
  function stateFor(root) {
    var st = states.get(root);
    if (!st) { st = { email: '' }; states.set(root, st); }
    return st;
  }

  // ── Config, read fresh off the root at event time ──
  function configFor(root) {
    var d = root.dataset;
    return {
      companyId: d.hp2cCompanyId || '',
      emailList: d.hp2cEmailList || '',
      smsList:   d.hp2cSmsList   || '',
      codeEmail: d.hp2cCodeEmail || '',
      codeSms:   d.hp2cCodeSms   || '',
      sectionId: d.hp2cRoot      || '',
      template:  d.hp2cTemplate  || ''
    };
  }

  // ── Transport ──
  // Hard 2500ms ceiling per call. A marketing POST must never be the reason a
  // visitor sits watching a disabled button. The underlying request is not
  // aborted, so a slow-but-successful enqueue still lands.
  function withTimeout(promise, label) {
    var timer;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error(label + ' timed out after ' + CALL_TIMEOUT_MS + 'ms'));
      }, CALL_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(function () { clearTimeout(timer); });
  }

  // ── Validation ──
  function isEmail(v) {
    return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  }
  // Copied verbatim from sections/persona-popup.liquid so a number entered on
  // either surface normalizes identically. Returns null for anything it cannot
  // make sense of, and a null number is never sent.
  function normalizePhone(v) {
    if (!v) return null;
    var digits = String(v).replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    if (digits.length >= 8 && digits.length <= 15) return '+' + digits;
    return null;
  }

  // ── Properties ──
  // Attribution first; the hand-authored capture_* keys win on any collision.
  // VioriAttribution is loaded site-wide from theme.liquid, but guard anyway so
  // a page without it still submits, just unattributed.
  function buildProperties(cfg) {
    var attribution = {};
    try {
      if (window.VioriAttribution && typeof window.VioriAttribution.snapshot === 'function') {
        attribution = window.VioriAttribution.snapshot() || {};
      }
    } catch (ex) {
      console.warn('[hp2-capture] attribution snapshot unavailable:', ex);
    }

    var merged = Object.assign({}, attribution, {
      capture_surface: 'homepage_capture_band',
      capture_section_id: cfg.sectionId,
      capture_page_path: window.location.pathname,
      capture_template: cfg.template
    });

    // Drop empties. snapshot() already omits most blanks, but its null
    // viori_session_id — and capture_template on a page whose section root
    // somehow carries no template — still need filtering. Klaviyo should not
    // carry empty-string properties.
    var out = {};
    for (var k in merged) {
      if (!merged.hasOwnProperty(k)) continue;
      var v = merged[k];
      if (v === null || v === undefined) continue;
      var str = String(v);
      if (!str) continue;
      out[k] = str;
    }
    return out;
  }

  function phase1Attrs(cfg, email) {
    var attrs = {
      email: email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    var props = buildProperties(cfg);
    if (Object.keys(props).length) attrs.properties = props;
    return attrs;
  }

  // The phase-1 email rides along so this POST merges the SMS consent onto the
  // same Klaviyo profile instead of creating a second, phone-only one.
  function phase2Attrs(cfg, email, phoneE164) {
    var attrs = {
      email: email,
      phone_number: phoneE164,
      subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    var props = buildProperties(cfg);
    if (Object.keys(props).length) attrs.properties = props;
    return attrs;
  }

  // ── Klaviyo POST ──
  function buildPayload(listId, profileAttrs) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Homepage Capture Band',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttrs
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };
  }

  // A 400 is a deterministic validation failure (bad email / bad number) — a
  // second identical request cannot change it, so it is surfaced immediately
  // rather than retried.
  function ValidationError(message) { this.name = 'ValidationError'; this.message = message; }
  ValidationError.prototype = Object.create(Error.prototype);

  // Resolves only on a confirmed 2xx.
  function postToKlaviyo(cfg, listId, profileAttrs, badInputMessage) {
    if (!cfg.companyId || !listId) {
      return Promise.reject(new Error('Klaviyo company_id or list_id is missing'));
    }
    return withTimeout(fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.companyId), {
      method: 'POST',
      headers: {
        // Revision 2025-04-15 accepts the current 'subscriptions' field shape
        // and returns 202 on enqueue. Older revisions 400 on it.
        'revision': REVISION,
        'content-type': 'application/json'
      },
      body: JSON.stringify(buildPayload(listId, profileAttrs))
    }).then(function (res) {
      if (res.status === 202 || res.status === 200 || res.status === 204) return res;
      if (res.status === 400) {
        return res.text().then(function () { throw new ValidationError(badInputMessage); });
      }
      return res.text().then(function (t) {
        throw new Error('Klaviyo ' + res.status + ': ' + t.slice(0, 240));
      });
    }), 'Klaviyo subscription POST');
  }

  // Exactly one retry, 1000ms apart, for anything that is not a 400 — timeout,
  // network error, 5xx. If that also fails the caller fails closed.
  function postWithRetry(cfg, listId, profileAttrs, badInputMessage) {
    return postToKlaviyo(cfg, listId, profileAttrs, badInputMessage).catch(function (err) {
      if (err instanceof ValidationError) throw err;   // no retry on a 400
      console.warn('[hp2-capture] attempt 1 failed — retrying once in ' + RETRY_DELAY_MS + 'ms:', err);
      return new Promise(function (resolve) { setTimeout(resolve, RETRY_DELAY_MS); })
        .then(function () { return postToKlaviyo(cfg, listId, profileAttrs, badInputMessage); });
    });
  }

  // ── UI helpers ──
  function showError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }
  function clearError(el) {
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  function goToPhase2(root) {
    var p1 = root.querySelector('[data-hp2c-phase="1"]');
    var p2 = root.querySelector('[data-hp2c-phase="2"]');
    if (p1) p1.hidden = true;
    if (p2) p2.hidden = false;
    setTimeout(function () {
      var phone = root.querySelector('[data-hp2c-phone]');
      if (phone) phone.focus();
    }, 80);
  }

  // Fill-one-fallback: if the tier's code setting is blank, show the other
  // tier's rather than an empty pill.
  function showSuccess(root, smsCompleted) {
    var cfg = configFor(root);
    var code = smsCompleted ? (cfg.codeSms || cfg.codeEmail) : (cfg.codeEmail || cfg.codeSms);
    var p1 = root.querySelector('[data-hp2c-phase="1"]');
    var p2 = root.querySelector('[data-hp2c-phase="2"]');
    var successEl = root.querySelector('[data-hp2c-success]');
    var codeEl = successEl ? successEl.querySelector('[data-hp2c-code]') : null;
    if (codeEl) codeEl.textContent = code;
    if (p1) p1.hidden = true;
    if (p2) p2.hidden = true;
    if (successEl) successEl.hidden = false;
  }

  // ── Phase 1 (email) ──
  function submitPhase1(root) {
    var cfg      = configFor(root);
    var emailInp = root.querySelector('[data-hp2c-email]');
    var btn      = root.querySelector('[data-hp2c-submit-1]');
    var err      = root.querySelector('[data-hp2c-error="1"]');
    if (!emailInp || !btn) return;
    if (btn.disabled) return;            // a POST is already in flight

    clearError(err);
    var emailVal = emailInp.value.trim();
    if (!isEmail(emailVal)) {
      showError(err, 'Please check your email address.');
      return;
    }

    btn.disabled = true;
    var origLabel = btn.textContent;
    btn.textContent = 'Sending…';

    postWithRetry(cfg, cfg.emailList, phase1Attrs(cfg, emailVal), 'Please check your email address.')
      .then(function () {
        // Confirmed 2xx — only now does the visitor count as captured.
        stateFor(root).email = emailVal;
        if (window.gtag) gtag('event', 'email_capture', { source: 'homepage_band' });
        goToPhase2(root);
        btn.disabled = false;
        btn.textContent = origLabel;
      })
      .catch(function (err2) {
        // Fail closed. Both attempts failed, so the visitor is NOT on the list —
        // advancing would hand out a code for a subscription that never
        // happened. The typed value stays in the field so they can retry.
        console.warn('[hp2-capture] phase 1 failed:', err2);
        btn.disabled = false;
        btn.textContent = origLabel;
        showError(err, (err2 && err2.name === 'ValidationError') ? err2.message : 'Something went wrong — try again.');
      });
  }

  // ── Phase 2 (SMS upsell) ──
  function submitPhase2(root) {
    var cfg      = configFor(root);
    var phoneInp = root.querySelector('[data-hp2c-phone]');
    var btn      = root.querySelector('[data-hp2c-submit-2]');
    var err      = root.querySelector('[data-hp2c-error="2"]');
    if (!phoneInp || !btn) return;
    if (btn.disabled) return;            // a POST is already in flight

    clearError(err);
    var phoneNormalized = normalizePhone(phoneInp.value.trim());
    if (!phoneNormalized) {
      // Stay on phase 2. A number we cannot parse is never sent.
      showError(err, 'Please check your phone number.');
      return;
    }

    btn.disabled = true;
    var origLabel = btn.textContent;
    btn.textContent = 'Sending…';

    postWithRetry(cfg, cfg.smsList, phase2Attrs(cfg, stateFor(root).email, phoneNormalized), 'Please check your phone number.')
      .then(function () {
        if (window.gtag) gtag('event', 'sms_capture', { source: 'homepage_band' });
        showSuccess(root, true);
      })
      .catch(function (err2) {
        // Fail closed on the SMS tier only. The email is already captured, so
        // this must not cost it: we stay on phase 2 with a working skip link,
        // and the visitor can still take the email-tier code.
        console.warn('[hp2-capture] phase 2 failed:', err2);
        btn.disabled = false;
        btn.textContent = origLabel;
        showError(err, (err2 && err2.name === 'ValidationError') ? err2.message : 'Something went wrong — try again.');
      });
  }

  // ── Copy the code ──
  function fallbackCopy(text, cb) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      if (cb) cb();
    } catch (e) {}
  }
  function copyCode(root, copyBtn) {
    var codeEl = root.querySelector('[data-hp2c-code]');
    if (!codeEl) return;
    var code = (codeEl.textContent || '').trim();
    if (!code) return;
    var done = function () {
      copyBtn.textContent = copyBtn.getAttribute('data-copied-label') || 'Copied!';
      setTimeout(function () {
        copyBtn.textContent = copyBtn.getAttribute('data-copy-label') || 'Copy';
      }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(function () { fallbackCopy(code, done); });
    } else {
      fallbackCopy(code, done);
    }
  }

  // ── Delegated listeners ──
  // Every one of these resolves its own root from the event target, so nothing
  // is cached and any number of bands can coexist on a page.
  function rootFrom(node) {
    return (node && node.closest) ? node.closest('[data-hp2c-root]') : null;
  }

  function onClick(e) {
    var root = rootFrom(e.target);
    if (!root) return;
    var t = e.target;
    if (t.closest('[data-hp2c-submit-1]')) { e.preventDefault(); submitPhase1(root); return; }
    if (t.closest('[data-hp2c-submit-2]')) { e.preventDefault(); submitPhase2(root); return; }
    if (t.closest('[data-hp2c-skip]'))     { e.preventDefault(); showSuccess(root, false); return; }
    var copyBtn = t.closest('[data-hp2c-copy]');
    if (copyBtn) { e.preventDefault(); copyCode(root, copyBtn); return; }
  }

  // Belt and braces. The band has no <form> of its own any more, so nothing
  // here should ever fire — but if a theme change ever wraps the band in an
  // ancestor form, a stray Enter or submit must not navigate the page and post
  // the address off to Shopify instead of Klaviyo.
  function onSubmit(e) {
    var form = e.target;
    if (!form || form.nodeType !== 1) return;
    // The form may be inside the band, or an ancestor wrapping it.
    var root = rootFrom(form) ||
               (form.querySelector ? form.querySelector('[data-hp2c-root]') : null);
    if (!root) return;
    e.preventDefault();
    var p2 = root.querySelector('[data-hp2c-phase="2"]');
    if (p2 && !p2.hidden) submitPhase2(root); else submitPhase1(root);
  }

  // Enter-to-submit. Removing the native <form> also removed the browser's
  // implicit submission, and an email field that ignores the Enter key reads as
  // broken. Routed through the same guarded submit paths as the buttons.
  function onKeydown(e) {
    if (e.key !== 'Enter') return;
    var root = rootFrom(e.target);
    if (!root) return;
    var t = e.target;
    if (t.closest('[data-hp2c-email]')) { e.preventDefault(); submitPhase1(root); return; }
    if (t.closest('[data-hp2c-phone]')) { e.preventDefault(); submitPhase2(root); return; }
  }

  document.addEventListener('click',   onClick,   true);
  document.addEventListener('submit',  onSubmit,  true);
  document.addEventListener('keydown', onKeydown, true);
})();
